import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumbs } from '@/components/news/Breadcrumbs';
import { generateArticleBreadcrumbs, toLocalIso, getSignificantModifiedDate } from '@/lib/structured-data';
import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Eye, 
  Share2, 
  ArrowLeft, 
  ArrowRight,
  Facebook,
  Linkedin,
  Link as LinkIcon,
  MessageCircle,
  ChevronLeft,
  Mail,
  Send,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { NewsLayout } from '@/layouts/NewsLayout';
import { CategoryBadge } from '@/components/news/CategoryBadge';

import { SponsoredBanner } from '@/components/news/SponsoredBanner';
import { PostArticleRecommendations } from '@/components/news/PostArticleRecommendations';
import { ZabavaCTA } from '@/components/news/ZabavaCTA';
import { NewsletterCTA } from '@/components/news/NewsletterCTA';
import { FloatingShareBar } from '@/components/news/FloatingShareBar';
import { ArticleSidebar } from '@/components/news/ArticleSidebar';
import { ArticleBodyWithAds } from '@/components/news/ArticleBodyWithAds';
import { ArticleLightbox, extractArticleImages } from '@/components/news/ArticleLightbox';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Article, Category, Author } from '@/lib/types';
import { useArticleDisplaySettings, formatArticleDate } from '@/hooks/useArticleDisplaySettings';
import { useSiteSettings } from '@/hooks/useSiteSettings';

// Helper to transform DB response to Article type
function transformArticle(data: any): Article {
  return {
    ...data,
    is_featured: data.is_featured ?? false,
    is_editors_pick: data.is_editors_pick ?? false,
    is_sponsored: data.is_sponsored ?? false,
    view_count: data.view_count ?? 0,
    share_count: data.share_count ?? 0,
    trending_score: data.trending_score ?? 0,
    reading_time: data.reading_time ?? 5,
    category: data.categories || null,
    author: data.profiles || null,
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: displaySettings } = useArticleDisplaySettings();
  const { data: siteSettings } = useSiteSettings();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const articleContentRef = useRef<HTMLDivElement>(null);

  // Fetch the article
  const { data: article, isLoading, error } = useQuery({
    queryKey: ['article', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          categories (*),
          profiles:author_id (*)
        `)
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      
      if (error) throw error;
      return transformArticle(data);
    },
    enabled: !!slug,
  });

  // Fetch article tags
  const { data: articleTags = [] } = useQuery({
    queryKey: ['article-tags', article?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_tags')
        .select('tag_id, tags(*)')
        .eq('article_id', article!.id);
      
      if (error) throw error;
      return data.map((at: any) => at.tags).filter(Boolean);
    },
    enabled: !!article?.id,
  });

  // Fetch related articles (same category)
  const { data: relatedArticles = [] } = useQuery({
    queryKey: ['related-articles', article?.category_id, article?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          categories (*),
          profiles:author_id (*)
        `)
        .eq('status', 'published')
        .eq('category_id', article!.category_id!)
        .neq('id', article!.id)
        .order('publish_date', { ascending: false })
        .limit(12);
      
      if (error) throw error;
      return data.map(transformArticle);
    },
    enabled: !!article?.category_id && !!article?.id,
  });

  // Fetch next/previous articles
  const { data: navigation } = useQuery({
    queryKey: ['article-navigation', article?.publish_date],
    queryFn: async () => {
      const [prevResult, nextResult] = await Promise.all([
        supabase
          .from('articles')
          .select('id, title, slug')
          .eq('status', 'published')
          .lt('publish_date', article!.publish_date!)
          .order('publish_date', { ascending: false })
          .limit(1)
          .single(),
        supabase
          .from('articles')
          .select('id, title, slug')
          .eq('status', 'published')
          .gt('publish_date', article!.publish_date!)
          .order('publish_date', { ascending: true })
          .limit(1)
          .single(),
      ]);
      
      return {
        prev: prevResult.data,
        next: nextResult.data,
      };
    },
    enabled: !!article?.publish_date,
  });

  // Increment view count (simplified - just update directly)
  useQuery({
    queryKey: ['increment-view', article?.id],
    queryFn: async () => {
      // Silently try to increment view count
      try {
        await supabase
          .from('articles')
          .update({ view_count: (article?.view_count || 0) + 1 })
          .eq('id', article!.id);
      } catch {}
      return true;
    },
    enabled: !!article?.id,
    staleTime: Infinity,
  });

  const lightboxImages = useMemo(() => {
    if (!article) return [];
    return extractArticleImages(
      article.featured_image,
      article.featured_image_caption,
      article.body
    );
  }, [article]);

  // Attach click handlers to body images for lightbox
  useEffect(() => {
    const container = articleContentRef.current;
    if (!container || lightboxImages.length === 0) return;

    const handleImgClick = (e: Event) => {
      const img = e.currentTarget as HTMLImageElement;
      const src = img.getAttribute('src');
      if (!src) return;
      const idx = lightboxImages.findIndex((li) => li.src === src);
      if (idx !== -1) {
        e.preventDefault();
        setLightboxIndex(idx);
      }
    };

    const imgs = container.querySelectorAll('img');
    imgs.forEach((img) => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', handleImgClick);
    });

    return () => {
      imgs.forEach((img) => {
        img.removeEventListener('click', handleImgClick);
      });
    };
  }, [lightboxImages, article]);

  const handleShare = async (platform: string) => {
    const canonicalBase = (siteSettings as any)?.canonical_url?.replace(/\/$/, '');
    const path = window.location.pathname;
    const url = canonicalBase ? `${canonicalBase}${path}` : window.location.href;
    const title = article?.title || '';
    const excerpt = article?.excerpt || '';
    const text = `${title}\n${excerpt}`.trim();
    
    const shareUrls: Record<string, string> = {
      x: `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`,
      messenger: `fb-messenger://share/?link=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(excerpt)}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title}\n${url}`)}`,
      viber: `viber://forward?text=${encodeURIComponent(`${title}\n${url}`)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      gmail: `https://mail.google.com/mail/?view=cm&su=${encodeURIComponent(title)}&body=${encodeURIComponent(`${excerpt}\n\n${url}`)}`,
    };

    // Native share on mobile
    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({ title, text: excerpt, url });
        return;
      } catch {}
    }

    if (platform === 'copy') {
      await navigator.clipboard.writeText(url);
      toast.success('Povezava kopirana!');
    } else if (platform === 'messenger') {
      // Messenger: try fb-messenger protocol, fallback to web
      const mobileUrl = shareUrls.messenger;
      const webUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&redirect_uri=${encodeURIComponent(url)}`;
      
      // Try mobile protocol first
      const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      window.open(isMobileDevice ? mobileUrl : webUrl, '_blank', 'noopener,noreferrer,width=600,height=500');
    } else {
      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
    }
  };

  // Display settings with safe defaults
  const showAuthorAvatar = displaySettings?.showAuthorAvatar ?? true;
  const showAuthorName = displaySettings?.showAuthorName ?? true;
  const showAuthorBox = displaySettings?.showAuthorBox ?? true;
  const showViewCount = displaySettings?.showViewCount ?? true;
  const showReadingTime = displaySettings?.showReadingTime ?? true;
  const showPublishDate = displaySettings?.showPublishDate ?? true;
  const showShareButtons = displaySettings?.showShareButtons ?? true;

  if (isLoading) {
    return (
      <NewsLayout>
        <div className="container py-12">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="aspect-video w-full mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </NewsLayout>
    );
  }

  if (error || !article) {
    return (
      <NewsLayout>
        <div className="container py-24 text-center">
          <h1 className="text-2xl font-serif font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/')}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
        </div>
      </NewsLayout>
    );
  }

  const publishDate = article.publish_date ? new Date(article.publish_date) : new Date();
  const formattedDate = displaySettings 
    ? formatArticleDate(publishDate, displaySettings.dateRegion, displaySettings.timeFormat, true, displaySettings.siteTimezone)
    : formatArticleDate(publishDate, 'EU', '24h', true, 'Europe/Ljubljana');

  // Build canonical URL
  const canonicalBase = (siteSettings as any)?.canonical_url?.replace(/\/$/, '') || window.location.origin;
  const canonicalUrl = `${canonicalBase}/article/${article.slug}`;
  const siteName = (siteSettings as any)?.site_name || 'BumNovice';
  const logoUrl = (siteSettings as any)?.logo_url || `${canonicalBase}/images/bumnovice-logo-main.png`;
  
  // OG description: prefer meta_description (short, punchy) → excerpt → fallback
  const ogDescription = (article.meta_description?.trim() || article.excerpt?.trim() || article.title).slice(0, 160);

  // Absolute image URL
  const featuredImageAbsolute = article.featured_image
    ? (article.featured_image.startsWith('http') ? article.featured_image : `${canonicalBase}${article.featured_image}`)
    : null;

  // OG image: use the article's featured image directly (Google requires raster, not SVG)
  const ogImageUrl = featuredImageAbsolute || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/og-image?slug=${encodeURIComponent(article.slug)}`;

  // JSON-LD Schema for SEO (Google News NewsArticle)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.excerpt || article.meta_description || '',
    "image": featuredImageAbsolute ? [featuredImageAbsolute] : [ogImageUrl],
    "datePublished": toLocalIso(article.publish_date),
    "dateModified": getSignificantModifiedDate(article.publish_date, article.updated_at),
    "author": article.author ? [{
      "@type": "Person",
      "name": article.author.display_name,
      ...(article.author.website ? { "url": article.author.website } : {}),
    }] : [{ "@type": "Organization", "name": siteName }],
    "publisher": {
      "@type": "Organization",
      "name": siteName,
      "logo": {
        "@type": "ImageObject",
        "url": logoUrl
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  return (
    <NewsLayout>
      {/* Dynamic OG Meta Tags */}
      <Helmet>
        <title>{article.meta_title || article.title} | {siteName}</title>
        <meta name="description" content={ogDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* Open Graph — optimized for tabloid sharing */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:locale" content="sl_SI" />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:secure_url" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={article.title} />
        <meta property="article:published_time" content={toLocalIso(article.publish_date) || ''} />
        <meta property="article:modified_time" content={getSignificantModifiedDate(article.publish_date, article.updated_at) || ''} />
        {article.category && <meta property="article:section" content={article.category.name} />}
        {article.author && <meta property="article:author" content={article.author.display_name} />}
        
        {/* Twitter/X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.meta_title || article.title} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="twitter:image:alt" content={article.title} />
      </Helmet>

      {/* SEO JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Floating Share Bar — desktop only, follows scroll */}
      {showShareButtons && (
        <FloatingShareBar title={article.title} excerpt={article.excerpt || ''} url={canonicalUrl} />
      )}

      <article className="pb-16">
        {/* Breadcrumb */}
        <div className="container py-6">
          <Breadcrumbs
            items={generateArticleBreadcrumbs({
              title: article.title,
              slug: article.slug,
              categoryName: article.category?.name,
              categorySlug: article.category?.slug,
            })}
          />
        </div>

        {/* Article Header */}
        <header className="container mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            {article.category && (
              <CategoryBadge category={article.category} className="mb-4" />
            )}
            
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {article.title}
            </h1>
            
            {article.excerpt && (
              <p className="text-xl text-foreground leading-relaxed mb-6 font-bold">
                {article.excerpt}
              </p>
            )}

            {/* Author and Meta */}
            <div className="flex flex-wrap items-center gap-6 pb-6 border-b border-border">
              {showAuthorName && article.author && (
                <div className="flex items-center gap-3">
                  {showAuthorAvatar && (
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={article.author.avatar_url || undefined} alt={article.author.display_name} />
                      <AvatarFallback>{article.author.display_name.charAt(0)}</AvatarFallback>
                    </Avatar>
                  )}
                  <div>
                    <p className="font-medium">{article.author.display_name}</p>
                    {article.author.twitter_handle && (
                      <a 
                        href={`https://twitter.com/${article.author.twitter_handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-muted-foreground hover:text-primary"
                      >
                        @{article.author.twitter_handle}
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {showPublishDate && <span>{formattedDate}</span>}
                {showReadingTime && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {article.reading_time} min branja
                  </span>
                )}
                {showViewCount && (
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {article.view_count.toLocaleString()} ogledov
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </header>

        {/* Featured Image */}
        {article.featured_image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="container mb-8"
          >
            <div className="max-w-5xl mx-auto">
              <figure>
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full aspect-video object-cover rounded-xl cursor-pointer"
                  onClick={() => setLightboxIndex(0)}
                />
                {article.featured_image_caption && (
                  <figcaption className="text-sm text-muted-foreground text-center mt-3">
                    {article.featured_image_caption}
                  </figcaption>
                )}
              </figure>
            </div>
          </motion.div>
        )}

        {/* Ad Slot */}
        <div className="container mb-8">
          <div className="max-w-4xl mx-auto">
            <SponsoredBanner slotId="article-top" />
          </div>
        </div>

        {/* Article Content */}
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content */}
            <motion.div
              ref={articleContentRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-8"
            >
              <ArticleBodyWithAds html={article.body || ''} paragraphInterval={4} maxAds={3} />

              {/* Mid-article Ad */}
              <div className="my-8">
                <SponsoredBanner slotId="article-mid" />
              </div>

              {/* Tags */}
              {articleTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-border">
                  {articleTags.map((tag: any) => (
                    <Link
                      key={tag.id}
                      to={`/tag/${tag.slug}`}
                      className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      #{tag.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* Share Buttons — compact inline */}
              {showShareButtons && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-muted-foreground mr-1">Deli:</span>
                    {[
                      { id: 'facebook', icon: <Facebook className="h-4 w-4" />, color: '#1877F2' },
                      { id: 'messenger', icon: <MessageCircle className="h-4 w-4" />, color: '#0099FF' },
                      { id: 'whatsapp', icon: <MessageCircle className="h-4 w-4" />, color: '#25D366' },
                      { id: 'viber', icon: <Phone className="h-4 w-4" />, color: '#7360F2' },
                      { id: 'x', icon: <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>, color: '#000000' },
                      { id: 'telegram', icon: <Send className="h-4 w-4" />, color: '#0088cc' },
                      { id: 'copy', icon: <LinkIcon className="h-4 w-4" />, color: undefined },
                    ].map(({ id, icon, color }) => (
                      <button
                        key={id}
                        onClick={() => handleShare(id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110"
                        style={{ backgroundColor: color || 'hsl(var(--muted))', color: color ? 'white' : 'hsl(var(--foreground))' }}
                        aria-label={`Deli na ${id}`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Bio */}
              {showAuthorBox && article.author && article.author.bio && (
                <div className="mt-8 p-6 bg-muted/50 rounded-xl">
                  <div className="flex items-start gap-4">
                    {showAuthorAvatar && (
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={article.author.avatar_url || undefined} alt={article.author.display_name} />
                        <AvatarFallback className="text-xl">{article.author.display_name.charAt(0)}</AvatarFallback>
                      </Avatar>
                    )}
                    <div>
                      <h4 className="font-serif font-bold text-lg">O avtorju: {article.author.display_name}</h4>
                      <p className="text-muted-foreground mt-2">{article.author.bio}</p>
                      {article.author.twitter_handle && (
                        <a 
                          href={`https://x.com/${article.author.twitter_handle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-primary hover:underline mt-3"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> Sledi @{article.author.twitter_handle}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Article Navigation */}
              <nav className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                {navigation?.prev && (
                  <Link
                    to={`/article/${navigation.prev.slug}`}
                    className="group p-4 border border-border rounded-xl hover:border-primary transition-colors"
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <ArrowLeft className="h-4 w-4" /> Prejšnji članek
                    </div>
                    <p className="font-medium group-hover:text-primary transition-colors line-clamp-2">
                      {navigation.prev.title}
                    </p>
                  </Link>
                )}
                {navigation?.next && (
                  <Link
                    to={`/article/${navigation.next.slug}`}
                    className="group p-4 border border-border rounded-xl hover:border-primary transition-colors text-right md:col-start-2"
                  >
                    <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground mb-2">
                      Naslednji članek <ArrowRight className="h-4 w-4" />
                    </div>
                    <p className="font-medium group-hover:text-primary transition-colors line-clamp-2">
                      {navigation.next.title}
                    </p>
                  </Link>
                )}
              </nav>
            </motion.div>

            {/* Sidebar - Desktop */}
            <ArticleSidebar
              articleId={article.id}
              categoryId={article.category_id}
              tagIds={articleTags.map((t: any) => t.id)}
            />
          </div>
        </div>

        {/* Zabava CTA — mobile only, before recommendations */}
        <ZabavaCTA />

        {/* Post-Article Recommendations */}
        <div className="mt-12">
          <PostArticleRecommendations
            relatedArticles={relatedArticles}
            currentSlug={slug}
          />
        </div>

        {/* Mobile Sidebar Content */}
        <div className="container mt-12">
          <ArticleSidebar
            articleId={article.id}
            categoryId={article.category_id}
            tagIds={articleTags.map((t: any) => t.id)}
            isMobile
          />
        </div>

        {/* Bottom Ad */}
        <div className="container mt-12">
          <SponsoredBanner slotId="article-bottom" />
        </div>
      </article>

      {/* Lightbox */}
      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <ArticleLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          articleTitle={article.title}
          articleUrl={canonicalUrl}
          articleId={article.id}
          categoryId={article.category_id || undefined}
        />
      )}
    </NewsLayout>
  );
}
